/**
 * useGuideSession — session persistence (localStorage + server API) for CoPilotGuide.
 */

import { useErrorCapture } from '@tonnie/observability';
import { useCoPilotGuideState, useSaveGuideState } from '@tonnie/sdk';
import type { CoPilotGuideEngine } from '@tonnie/copilot';
import { useCallback, useEffect, useRef } from 'react';

import { saveSessionState } from '../guide-session';
import { GUIDE_SESSION_SAVE_DEBOUNCE_MS } from '../../../lib/constants';
import { useCopilotLogger } from '../../../lib/use-copilot-logger';
import type { IMessage } from '../types';

/**
 * Stop firing autosaves after this many CONSECUTIVE failures, to avoid
 * hammering an endpoint that is genuinely down. The counter resets on
 * any successful save — so a transient blip doesn't permanently brick
 * the session (which it did pre-2026-05-08 when MAX was 3 and the only
 * failure mode was the optimistic-concurrency cascade we've since
 * fixed at the SDK layer via await-refetch-and-retry-once).
 *
 * Keep generous enough that the new SDK-level retry path always has
 * room to recover; if we're still failing after this many attempts
 * something is seriously wrong (auth lost, server 5xx, etc.) and the
 * host should surface its `<FlowCompletion>` error / retry UI rather
 * than fire-and-forget.
 */
const MAX_CONSECUTIVE_SAVE_FAILURES = 8;

/**
 * Build a stable content fingerprint of the bits we POST. We use it
 * to dedupe back-to-back saves that produce an identical body — common
 * when an animation effect re-fires the save callback without any
 * underlying engine change. Skipping the no-op write avoids:
 *   • burning the optimistic-concurrency token (every save bumps
 *     rowVersion server-side; bumping it without a meaningful change
 *     would falsely trigger 409s on a peer tab),
 *   • a refetch storm on slow networks where the save's invalidation
 *     races the next save effect.
 *
 * Cheap by construction — just a JSON serialisation of what the
 * server already accepts. Not hashed because the resulting string is
 * compared byte-for-byte to the previous one we sent; collisions are
 * not a concern.
 */
function fingerprintSavePayload(body: {
  currentTaskId?: string;
  currentChatId?: string;
  completedTasks: string[];
  completedChats: string[];
  variables: Record<string, unknown>;
  chatMessagesLen: number;
  progress: number;
}): string {
  return JSON.stringify(body);
}

export function useGuideSession(
  flowId: string | undefined,
  persistSession: boolean,
  engine: CoPilotGuideEngine,
  chatMessages: IMessage[],
  journeyId?: string
) {
  const { captureException } = useErrorCapture();
  const log = useCopilotLogger('CP:Save', { flowId, journeyId });
  const persistRef = useRef(persistSession);
  persistRef.current = persistSession;
  const journeyIdRef = useRef(journeyId);
  journeyIdRef.current = journeyId;

  const { data: serverGuideState, isLoading: guideStateLoading } = useCoPilotGuideState(
    flowId ?? '',
    journeyId
  );
  const saveGuideStateMutation = useSaveGuideState();
  const saveGuideStateMutateRef = useRef(saveGuideStateMutation.mutate);
  saveGuideStateMutateRef.current = saveGuideStateMutation.mutate;
  const saveFailCountRef = useRef(0);
  const flowCompletedRef = useRef(false);
  // Fingerprint of the LAST successfully-shipped payload. We compare
  // each new candidate against it to skip identical re-saves —
  // necessary because effect re-fires (animation, focus events,
  // referential-identity churn on the chatMessages array) used to
  // trigger a server round-trip every time, even with no real change.
  // Each dropped no-op also avoids burning a rowVersion bump that a
  // peer tab would then mis-interpret as a conflict.
  const lastSavedFingerprintRef = useRef<string | null>(null);

  // (Pre-2026-05-08 a `conflictRetryInFlightRef` lived here to gate a
  // 250ms-setTimeout-based retry on `GuideStateConflictSdkError`. That
  // logic moved DOWN into `withConflictRetry` inside the SDK's
  // `useSaveGuideState` mutation hook, where it can `await
  // refetchQueries` rather than guess at the network round-trip with
  // a magic number. By the time `onError` fires here the SDK has
  // already retried once with a fresh rowVersion — if we still landed
  // here it's a real persistent conflict and another retry would just
  // hammer the endpoint.)

  const saveSession = useCallback(() => {
    if (!persistRef.current || !flowId) return;
    if (saveFailCountRef.current >= MAX_CONSECUTIVE_SAVE_FAILURES) return;
    if (flowCompletedRef.current) return;
    const state = engine.getState();

    const fingerprint = fingerprintSavePayload({
      currentTaskId: state.currentTaskId ?? undefined,
      currentChatId: state.currentChatId ?? undefined,
      completedTasks: Array.from(state.completedTasks),
      completedChats: Array.from(state.completedChats),
      variables: state.variables,
      chatMessagesLen: chatMessages.length,
      progress: Math.min(engine.getCompletionPercentage(), 99),
    });
    if (fingerprint === lastSavedFingerprintRef.current) {
      log.debug('skip.unchanged');
      return;
    }

    log.info('fire', {
      chatId: state.currentChatId ?? undefined,
      data: {
        currentTaskId: state.currentTaskId,
        completedChats: Array.from(state.completedChats).length,
        msgs: chatMessages.length,
        progress: Math.min(engine.getCompletionPercentage(), 99),
      },
    });
    saveSessionState(flowId, journeyIdRef.current ?? null, chatMessages, state);
    saveGuideStateMutateRef.current(
      {
        flowId,
        journeyId: journeyIdRef.current,
        state: {
          currentTaskId: state.currentTaskId ?? undefined,
          currentChatId: state.currentChatId ?? undefined,
          completedTasks: Array.from(state.completedTasks),
          completedChats: Array.from(state.completedChats),
          variables: state.variables,
          chatHistory: state.chatHistory.map((e) => ({
            ...e,
            timestamp:
              e.timestamp instanceof Date ? e.timestamp.toISOString() : String(e.timestamp),
            type: e.type as string,
          })),
          // Persist the rendered UI bubbles too. Without this, a user
          // resuming from a different browser / cleared storage sees a
          // blank chat panel even though the engine state hydrates
          // correctly. Cast through `unknown` because IMessage is a
          // frontend type and the SDK contract is opaque JSON
          // (`Array<Record<string, unknown>>`) — the server stores
          // whatever we send, the engine reads it back verbatim.
          chatMessages: chatMessages as unknown as Array<Record<string, unknown>>,
          status: 'active',
          progress: Math.min(engine.getCompletionPercentage(), 99),
          journeyInstanceId: journeyIdRef.current,
        },
      },
      {
        onSuccess: () => {
          saveFailCountRef.current = 0;
          lastSavedFingerprintRef.current = fingerprint;
          log.info('server.ok');
        },
        onError: (error) => {
          // The SDK's `withConflictRetry` has already done a refetch +
          // retry-once on optimistic-concurrency conflicts. If we landed
          // here, either:
          //   (a) a NON-conflict error (network blip, 401, 5xx), or
          //   (b) the retry hit a SECOND consecutive conflict — i.e.
          //       a peer tab really is racing this row.
          // Both cases warrant the failure counter; another FE-level
          // retry would be a third attempt against the same race and
          // wouldn't change anything. Surface to Sentry and back off.
          captureException(error instanceof Error ? error : new Error(String(error)), {
            component: 'CoPilotGuide',
            action: 'saveSession',
          });
          saveFailCountRef.current += 1;

          log.warn('server.failed', {
            data: {
              err: (error as Error).message,
              failCount: saveFailCountRef.current,
            },
          });
        },
      }
    );
  }, [persistRef, flowId, engine, chatMessages, captureException, log]);

  // Debounced save on message changes — EXCEPT across a task transition,
  // which is written immediately.
  //
  // Debouncing message chatter is right: a typing chain emits several bubbles
  // in a burst and one write at the end is enough. But the SAME timer was also
  // deferring the `currentChatId` / `currentTaskId` advance, and that is the
  // value `restore` treats as truth. So for 2s after "Let's start" the UI was
  // on task1 while the server still said task0_intro — and any engine remount
  // in that window (the host does MOUNT → UNMOUNT → MOUNT on entry, and under
  // load that churn can land late) restored the stale row and threw the user
  // back to the Introduction with their progress bar still showing 33%.
  //
  // That is the block-07 "Let's start freezes" defect: not a hang, a silent
  // rewind. `hasStartedRef` in `use-co-pilot-guide` cannot help — a real
  // remount resets the ref along with everything else.
  //
  // Flushing on transition closes the window at the source. A task advance is
  // rare (once per step) and user-initiated, so the extra write costs nothing
  // and buys a server row that always agrees with the screen.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevChatMessagesLenRef = useRef(0);
  const prevChatIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (chatMessages.length === 0) return;
    const currentChatId = engine.getState().currentChatId ?? null;
    const advanced = prevChatIdRef.current !== null && currentChatId !== prevChatIdRef.current;
    prevChatIdRef.current = currentChatId;

    if (chatMessages.length === prevChatMessagesLenRef.current && !advanced) return;
    const wasFirstSave = prevChatMessagesLenRef.current === 0;
    prevChatMessagesLenRef.current = chatMessages.length;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    if (advanced) {
      log.debug('flush.transition', { data: { chatId: currentChatId } });
      saveSession();
      return;
    }

    log.debug('schedule', {
      data: { debounceMs: GUIDE_SESSION_SAVE_DEBOUNCE_MS, msgs: chatMessages.length, wasFirstSave },
    });
    saveTimerRef.current = setTimeout(() => {
      saveSession();
      saveTimerRef.current = null;
    }, GUIDE_SESSION_SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [chatMessages, saveSession, flowId, log, engine]);

  /** Mark flow as completed — prevents further saves, clears pending timers */
  const markFlowCompleted = useCallback(() => {
    flowCompletedRef.current = true;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  }, []);

  // (Pre-2026-05-08 a `saveCompletedState` callback lived here, used
  // by the engine sentinel handlers to flip status='completed' before
  // the host's `onComplete` ran. That fired a SECOND parallel
  // mutation against the same row in the same micro-tick as the
  // host's `useGuideHostFinalize`, both reading the same cached
  // rowVersion → conflict. The `useGuideHostFinalize` path already
  // writes status='completed' atomically inside the same DB
  // transaction as the widget snapshot, so the duplicate is dropped
  // entirely. See `use-co-pilot-guide.ts` sentinel branches.)

  /** Save a blank/reset state to server */
  const saveResetState = useCallback(
    (
      variables: Record<string, unknown>,
      status: 'active' | 'completed' = 'active',
      progress = 0
    ) => {
      if (!flowId) return;
      saveGuideStateMutateRef.current({
        flowId,
        journeyId: journeyIdRef.current,
        state: {
          completedTasks: [],
          completedChats: [],
          variables,
          chatHistory: [],
          status,
          progress,
          journeyInstanceId: journeyIdRef.current,
        },
      });
    },
    [flowId]
  );

  return {
    serverGuideState,
    guideStateLoading,
    saveSession,
    saveTimerRef,
    flowCompletedRef,
    markFlowCompleted,
    saveResetState,
  };
}
